/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any */

import { wrap } from 'comlink';
import { DateTime } from 'luxon';

import { TRIP_OPERATION, TRIPS } from '@/constants';
import { container } from '@/container';
import { UserController } from '@/controllers/UserController';
import CONSTANTS from '@/helpers/constants';
import { Logger } from '@/services/Logger';
import { RecurringTripUpdateReqMsg } from '@/services/signalr/MessageTypes/RecurringTrip';
import { Signalr } from '@/services/signalr/Signalr';
import { plainObject } from '@/utils/plainObject';

const logger = container.get(Logger);
const userController = container.get(UserController);

/**
 * Load the web worker for message construction.
 * @type {{
 *   createTripMessage: (function(trip: object): Promise<object>),
 * }}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const worker: any = wrap(new Worker(new URL('../../store/events/updateTrip.worker.js', import.meta.url), { type: 'module' }));

export interface CreateRecurringTripParams {
  trip: Record<string, unknown>;
}

/**
 * Create a recurring trip
 */
export async function createRecurringTrip(params: CreateRecurringTripParams): Promise<unknown> {
  let response;
  try {
    const signalr = await container.getAsync(Signalr);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, no-param-reassign
    const { trip } = params;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, no-param-reassign
    trip.Who = userController.activeUser.fullName;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    const tripData = await worker.createTripMessage(plainObject(trip));

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const recurringMessage: any = {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      contextKey: (trip.operation === TRIP_OPERATION.EDIT_SERIES)
        ? CONSTANTS.UI.CONTEXTS.RECURRING_TRIP
        : CONSTANTS.UI.CONTEXTS.RECURRING_VIRTUAL_TRIP,
      recurringTrip: {
        tripData,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        recurrence: (trip.operation === TRIP_OPERATION.EDIT_SERIES)
          ? {
            recurStart: DateTime.fromISO(trip.recurStart as string).toUTC().toISO(), // Note: converting recurStart to UTC
            recurEnd: DateTime.fromISO(trip.recurEnd as string).toUTC().toISO(), // Note: converting recurEnd to UTC,
            recurFrequency: trip.recurFrequency,
            recurNumOccurrences: null,
            recurDaysOfWeek: trip.recurDaysOfWeek,
            recurringSeriesId: trip.recurringSeriesId,
            status: 1,
            recurMonthDay: null,
            recurMonth: null,
            recurOrdinal: null,
            rhildSeriesId: null,
            recurInterval: trip.recurInterval,
          }
          : null,
        recurringTripType: trip.recurringTripType,
        recurringTripId: trip.recurringTripId,
        childTrip: trip.returnPickupTime
          ? {
            pickupDateTime: DateTime.fromISO(trip.returnPickupTime as string).toJSDate().toISOString(),
            tripStatus: trip.returnWillCall ? TRIPS.STATUS.WILL_CALL : null,
          }
          : null,
      },
      operation: trip.operation,
    };

    response = await signalr.invoke(RecurringTripUpdateReqMsg, recurringMessage);

    return response;
  }
  catch (error) {
    logger.captureException(error as Error, { tags: { createTrip: 'createRecurringTrip' } });
    throw error;
  }
}
